import React from 'react';
import { MailCheck } from 'lucide-react';
import { deliveryState } from '@/lib/useClaimDemo';

const STATE_STYLE = { unconfirmed: 'border-amber-500/50 text-amber-200', unacknowledged: 'border-sky-500/50 text-sky-200', acknowledged: 'border-emerald-500/40 text-emerald-200' };
const codeStyle = (c) => (c === null ? 'bg-amber-500/15 text-amber-300' : c < 300 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300');

const fmt = (t) => new Date(t * 1000).toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
const short = (h) => `${h.slice(0, 10)}...${h.slice(-6)}`;

function Row({ k, v, id }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-4 gap-y-0.5 py-1.5 border-b border-white/5 text-sm">
      <span className="text-gray-500">{k}</span>
      <span className="font-mono text-xs text-gray-200 break-all" data-testid={id}>{v}</span>
    </div>
  );
}

function ReceiptRows({ current }) {
  return (
    <div data-testid="demo-receipt-rows">
      <Row k="Case" v={current.caseRef} id="demo-receipt-case" />
      <Row k="Claim ID" v={current.claimId} id="demo-receipt-claim-id" />
      <Row k="Received at" v={fmt(current.receipt.receivedAt)} id="demo-receipt-received-at" />
      <Row k="First status by" v={fmt(current.receipt.firstStatusBy)} id="demo-receipt-first-status-by" />
      <Row k="Request hash" v={short(current.receipt.requestHash)} id="demo-receipt-request-hash" />
      <Row k="Receipt hash" v={short(current.receipt.receiptHash)} id="demo-receipt-hash" />
      <Row k="Signature" v={short(current.receipt.signature)} id="demo-receipt-signature" />
    </div>
  );
}

export function DemoReceipt({ current, merchantKey, log }) {
  const state = current && deliveryState(current);
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-5" data-testid="demo-receipt-panel">
      <div>
        <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><MailCheck className="w-4 h-4 text-amber-300" /> 2. Delivery receipt</h2>
        <p className="text-xs text-gray-500">Signed by the demo merchant's throwaway key <span className="font-mono break-all" data-testid="demo-merchant-key">{merchantKey}</span>, made in this browser tab.</p>
      </div>
      {current ? (
        <div data-testid="demo-receipt">
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs text-gray-400">Spoken code</div>
              <div className="font-mono text-2xl tracking-[0.3em] text-amber-200" data-testid="demo-spoken-code">{current.spokenCode}</div>
            </div>
            <div className={`text-xs rounded-full border px-3 py-1 ${STATE_STYLE[state.id]}`} data-testid="demo-delivery-state">{state.label}</div>
          </div>
          <p className="text-xs text-gray-400 mb-3" data-testid="demo-delivery-state-detail">{state.detail}</p>
          {current.agentHasReceipt ? <ReceiptRows current={current} /> : (
            <p className="text-sm text-amber-100/80 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-3" data-testid="demo-receipt-missing">No receipt held. The agent can still compute the claim ID and spoken code itself, because they come from the claim, not from the merchant.</p>
          )}
        </div>

      ) : <p className="text-sm text-gray-500" data-testid="demo-receipt-empty">Send a claim to see its receipt.</p>}
      <div>
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Responses</div>
        <ul className="space-y-2" data-testid="demo-log">
          {log.map((l, i) => (
            <li key={l.id} className="flex gap-3 text-sm" data-testid={`demo-log-${i}`}>
              <span className={`font-mono text-xs rounded px-1.5 py-0.5 h-fit shrink-0 ${codeStyle(l.code)}`} data-testid={`demo-log-${i}-code`}>{l.code ?? 'lost'}</span>
              <span className="text-gray-300">{l.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
